import { FileButton } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import {
  BenefitDocumentDto,
  QualifyingLifeEventDto,
  SpecialEnrollmentReasonDto,
  SpecialEnrollmentReasonType,
} from '@zorro/clients';
import { Button } from '@zorro/design-system/v2/Button';
import { Checkbox } from '@zorro/design-system/v2/Checkbox';
import {
  DocumentCard,
  DocumentCardProps,
} from '@zorro/design-system/v2/DocumentCard';
import {
  formatDateEnLocale,
  getNow,
  parseDateISOThenFormatEnLocale,
} from '@zorro/shared/formatters';
import { qualifyingLifeEventLabelConfig } from '@zorro/types';
import { Box, Group, Stack, Text } from '@zorro/web/design-system';
import { downloadBenefitDocument, useMonolithQuery } from '@zorro/web/ui-utils';
import { Controller, UseFormReturn } from 'react-hook-form';

import { useAnalytics } from '../../../Analytics';
import {
  UploadableBenefitDocument,
  buildIchraLetterFileName,
  getDocumentKey,
  isPersistedDocument,
} from '../../../BenefitDocuments';
import { CarrierApplicationSubmitFormFields } from '../CarrierCompleteStepUtils';

// HealthSherpa accepts PDF, PNG and JPG only.
const ACCEPTED_FILE_TYPES = 'application/pdf,image/png,image/jpeg';

type DocumentRowProps = DocumentCardProps & {
  isChecked: boolean;
  onToggle?: () => void;
};

const DocumentRow = ({ isChecked, onToggle, ...card }: DocumentRowProps) => (
  <Group gap="sm" wrap="nowrap" align="center">
    <Checkbox
      checked={isChecked}
      isReadOnly={!onToggle}
      onChange={() => onToggle?.()}
      aria-label={card.fileName}
    />
    <Box flex={1} miw={0}>
      <DocumentCard {...card} variant="minimal" />
    </Box>
  </Group>
);

function buildEventSummary(
  specialEnrollment: SpecialEnrollmentReasonDto,
  qualifyingLifeEvent?: QualifyingLifeEventDto | null
): string {
  // The offer itself is the reason, so there are no reported/event dates to show.
  if (
    specialEnrollment.type === SpecialEnrollmentReasonType.OFFERED_ICHRA ||
    !qualifyingLifeEvent
  ) {
    return `Qualifying life event: Offered ICHRA. Effective ${parseDateISOThenFormatEnLocale(specialEnrollment.eventDate)}.`;
  }

  const label = qualifyingLifeEventLabelConfig[qualifyingLifeEvent.type];
  const reportedOn = formatDateEnLocale(qualifyingLifeEvent.createdAt);
  const eventDate = parseDateISOThenFormatEnLocale(
    qualifyingLifeEvent.occurredOn
  );

  return `Qualifying life event: ${label}. Reported ${reportedOn}, event date ${eventDate}.`;
}

type Props = {
  employeeId: string;
  onboardingPeriodId: string;
  specialEnrollment: SpecialEnrollmentReasonDto;
  qualifyingLifeEvent?: QualifyingLifeEventDto | null;
  documents: BenefitDocumentDto[];
  submissionForm: UseFormReturn<CarrierApplicationSubmitFormFields>;
  onboardingPeriodEffectiveFrom?: string;
};

/**
 * The proof the carrier needs for a mid-year start. Every document on the enrollment is listed,
 * with the QLE ones (or Zorro's ICHRA letter, when the offer itself is the reason) picked for the
 * agent — whatever stays checked is what goes to the carrier on submit.
 */
export const CarrierSpecialEnrollmentDocumentsSection = ({
  employeeId,
  onboardingPeriodId,
  specialEnrollment,
  qualifyingLifeEvent,
  documents,
  submissionForm,
  onboardingPeriodEffectiveFrom,
}: Props) => {
  const { sendEvent } = useAnalytics();
  const { control, watch, setValue } = submissionForm;
  const selectedDocuments = watch('specialEnrollmentDocuments');
  const selected = Array.isArray(selectedDocuments) ? selectedDocuments : [];

  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });

  const isOfferedIchra =
    specialEnrollment.type === SpecialEnrollmentReasonType.OFFERED_ICHRA;
  // Files picked here live in the form until the submission saves them on the enrollment.
  const addedFiles = selected.filter(
    (item): item is File => item instanceof File
  );
  // A submission saves the added files without refetching the enrollment, so keep the rows it
  // created listed (a retry after a failed submit would otherwise lose sight of them).
  const savedFileIds = new Set(documents.map(({ id }) => id));
  const savedFiles = selected
    .filter(isPersistedDocument)
    .filter(({ id }) => !savedFileIds.has(id));

  const setSelected = (next: UploadableBenefitDocument[]) =>
    setValue('specialEnrollmentDocuments', next, { shouldDirty: true });

  const isSelected = (document: BenefitDocumentDto) =>
    selected.some(
      (item) => isPersistedDocument(item) && item.id === document.id
    );

  const handleToggleDocument = (document: BenefitDocumentDto) =>
    setSelected(
      isSelected(document)
        ? selected.filter(
            (item) => !isPersistedDocument(item) || item.id !== document.id
          )
        : [...selected, document]
    );

  const handleAddFiles = (files: File[]) => {
    if (files.length === 0) return;

    setSelected([...selected, ...files]);
    void sendEvent('upload_special_enrollment_document', {
      onboarding_period_id: onboardingPeriodId,
      number_of_files: files.length,
    });
  };

  const handleRemoveFile = (file: File) =>
    setSelected(selected.filter((item) => item !== file));

  return (
    <Stack gap="md" mt="2rem">
      <Stack gap="xxs">
        <Text size="md" fw={600}>
          Special enrollment documentation
        </Text>
        <Text size="sm" c="var(--color-text-default-secondary)">
          {buildEventSummary(specialEnrollment, qualifyingLifeEvent)}
          <br />
          Select the supporting document(s) below to submit this application.
        </Text>
      </Stack>
      <Stack gap="sm">
        {isOfferedIchra && employee && onboardingPeriodEffectiveFrom && (
          <Controller
            control={control}
            name="withIchraLetterAsSepProof"
            render={({ field: { value, onChange } }) => (
              <DocumentRow
                isChecked={Boolean(value)}
                onToggle={() => onChange(!value)}
                fileName={buildIchraLetterFileName(
                  employee,
                  onboardingPeriodEffectiveFrom
                )}
                date={formatDateEnLocale(getNow())}
              />
            )}
          />
        )}
        {documents.map((document) => (
          <DocumentRow
            key={document.id}
            isChecked={isSelected(document)}
            onToggle={() => handleToggleDocument(document)}
            fileName={document.fileName}
            date={formatDateEnLocale(document.createdAt)}
            onDownload={() =>
              downloadBenefitDocument(document.documentUrl, document.fileName)
            }
          />
        ))}
        {savedFiles.map((document) => (
          <DocumentRow
            key={document.id}
            isChecked
            onToggle={() => handleToggleDocument(document)}
            fileName={document.fileName}
            date={formatDateEnLocale(document.createdAt)}
            onDownload={() =>
              downloadBenefitDocument(document.documentUrl, document.fileName)
            }
          />
        ))}
        {addedFiles.map((file) => (
          <DocumentRow
            key={getDocumentKey(file)}
            isChecked
            fileName={file.name}
            date={formatDateEnLocale(getNow())}
            onDelete={() => handleRemoveFile(file)}
          />
        ))}
        <Group justify="flex-end">
          <FileButton
            multiple
            accept={ACCEPTED_FILE_TYPES}
            onChange={handleAddFiles}
          >
            {(props) => (
              <Button
                {...props}
                variant="secondary"
                size="sm"
                leftSection={<IconPlus size={16} />}
              >
                Add another document
              </Button>
            )}
          </FileButton>
        </Group>
      </Stack>
    </Stack>
  );
};
